import React from "react";
import { Box } from "@mui/material";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { Offer, WarrantyPeriodCode, OfferPaymentTermsCode, ValidityPeriodCode } from "../../models/models";

interface OfferTermsCardProps {
  offer: Partial<Offer>;
}

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", py: "8px" }}>
    <Box sx={{ fontSize: "13px", fontWeight: 500, fontFamily: dt.font, color: dt.textSecondary, flex: "0 0 45%" }}>
      {label}
    </Box>
    <Box sx={{ fontSize: "13px", fontWeight: 600, fontFamily: dt.fontMono, color: dt.textPrimary, textAlign: "right", flex: "0 0 55%" }}>
      {value}
    </Box>
  </Box>
);

const OfferTermsCard: React.FC<OfferTermsCardProps> = ({ offer }) => {
  const { translate } = useTranslation(["serviceOffers"]);

  const getWarrantyPeriodLabel = (): string => {
    switch (offer.warrantyPeriodCode) {
      case WarrantyPeriodCode.thirtyDays: return translate("warrantyPeriodCodes.thirtyDays");
      case WarrantyPeriodCode.ninetyDays: return translate("warrantyPeriodCodes.ninetyDays");
      case WarrantyPeriodCode.sixMonths: return translate("warrantyPeriodCodes.sixMonths");
      case WarrantyPeriodCode.oneYear: return translate("warrantyPeriodCodes.oneYear");
      case WarrantyPeriodCode.custom: return offer.customWarrantyPeriod || "\u2014";
      default: return "\u2014";
    }
  };

  const getPaymentTermsLabel = (): string => {
    switch (offer.paymentTermsCode) {
      case OfferPaymentTermsCode.paymentOnCompletion: return translate("paymentTermsCodes.paymentOnCompletion");
      case OfferPaymentTermsCode.net30Days: return translate("paymentTermsCodes.net30Days");
      case OfferPaymentTermsCode.fiftyAdvanceFiftyCompletion: return translate("paymentTermsCodes.fiftyAdvanceFiftyCompletion");
      case OfferPaymentTermsCode.net60Days: return translate("paymentTermsCodes.net60Days");
      case OfferPaymentTermsCode.custom: return offer.customPaymentTerms || "\u2014";
      default: return "\u2014";
    }
  };

  const getValidityPeriodLabel = (): string => {
    switch (offer.validityPeriodCode) {
      case ValidityPeriodCode.threeDays: return translate("validityPeriodCodes.threeDays");
      case ValidityPeriodCode.fiveDays: return translate("validityPeriodCodes.fiveDays");
      case ValidityPeriodCode.sevenDays: return translate("validityPeriodCodes.sevenDays");
      case ValidityPeriodCode.fourteenDays: return translate("validityPeriodCodes.fourteenDays");
      case ValidityPeriodCode.thirtyDays: return translate("validityPeriodCodes.thirtyDays");
      case ValidityPeriodCode.custom: return offer.customValidityPeriod || "\u2014";
      default: return "\u2014";
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: dt.cardBg,
        border: `1px solid ${dt.cardBorder}`,
        borderRadius: dt.radius,
        padding: "20px",
      }}
    >
      <Box
        sx={{
          fontSize: "11px",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "1px",
          color: dt.textMuted,
          fontFamily: dt.font,
          mb: "12px",
        }}
      >
        {translate("termsAndConditions")}
      </Box>

      <DetailRow label={translate("warrantyPeriod")} value={getWarrantyPeriodLabel()} />
      <DetailRow label={translate("paymentTerms")} value={getPaymentTermsLabel()} />
      <DetailRow label={translate("offerValidityPeriod")} value={getValidityPeriodLabel()} />
    </Box>
  );
};

export default OfferTermsCard;
